import { useCallback, useRef } from 'react';
import { debounce } from '@mui/material';
import { Cancelable } from '@mui/utils/debounce';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { TypedUseLazyQuery } from '@reduxjs/toolkit/query/react';

import { mapDataToUndefinedIfSkip } from 'modules/api/utils/mapDataToUndefinedIfSkip';

export const DEFAULT_DEBOUNCE_TIME = 500;

export function useLazyQueryDebounce<A, B, C extends BaseQueryFn>(
  useLazyQuery: TypedUseLazyQuery<A, B, C>,
  wait = DEFAULT_DEBOUNCE_TIME,
) {
  const [getQuery, result] = useLazyQuery({
    selectFromResult: mapDataToUndefinedIfSkip,
  });

  const debounceRef = useRef<typeof getQuery & Cancelable>();

  const getDebouncedQuery = useCallback(
    async (
      args: Parameters<typeof getQuery>[0],
      preferCacheValue?: boolean,
      immediately?: boolean,
    ) => {
      if (immediately) {
        if (debounceRef.current) {
          debounceRef.current.clear();
        }
        return getQuery(args);
      }

      if (!debounceRef.current) {
        debounceRef.current = debounce(getQuery, wait);
      }

      return debounceRef.current(args, preferCacheValue);
    },
    [getQuery, wait],
  );

  return [getDebouncedQuery, result] as const;
}
